import { createApi } from "@reduxjs/toolkit/query/react";

import {
    ICheckOrganizationsAccessResponse,
    ICreateOrganizationBody,
    ICreateOrganizationResponse,
    IDeleteOrganizationBody,
    IDeleteOrganizationResponse,
    IGetOrganizationByIdQuery,
    IGetOrganizationByIdResponse,
    IGetOrganizationEmailReportsResponse,
    IGetOrganizationsQuery,
    IGetOrganizationsResponse,
    ISetOrganizationManagersBody,
    ISetOrganizationManagersResponse,
    ISetOrganizationProjectsBody,
    ISetOrganizationProjectsResponse,
    // ISetOrganizationManagersBody,
    // ISetOrganizationManagersResponse,
    IUpdateOrganizationBody,
    IUpdateOrganizationResponse,
} from "../../types/organizations";
import { getOrganizationsQueryString } from "../../utils/queries";
import { baseQueryWithIntercept } from "../connection/api";

const ENDPOINT_PREFIX = "organizations";

export const organizationsApi = createApi({
    reducerPath: "organizationsApi",
    baseQuery: baseQueryWithIntercept,
    tagTypes: ["Organizations", "Organization"],
    endpoints: build => ({
        getOrganizations: build.query<IGetOrganizationsResponse, IGetOrganizationsQuery>({
            query: query => ({
                url: `${ENDPOINT_PREFIX}?${getOrganizationsQueryString(query)}`,
            }),
            providesTags: (result, _error, _arg) =>
                result?.data.organizations
                    ? [...result.data.organizations.map(({ id }) => ({ type: "Organizations" as const, id })), "Organizations"]
                    : ["Organizations"],
        }),
        createOrganization: build.mutation<ICreateOrganizationResponse, ICreateOrganizationBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/`,
                method: "POST",
                body: data.data,
            }),
            invalidatesTags: ["Organizations"],
        }),
        getOrganizationById: build.query<IGetOrganizationByIdResponse, IGetOrganizationByIdQuery>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.organizationId}`,
                method: "GET",
            }),
            providesTags: (result, _error, arg) => [{ type: "Organization", id: arg.organizationId }],
        }),
        checkOrganizationsAccess: build.query<ICheckOrganizationsAccessResponse, void>({
            query: () => ({
                url: `${ENDPOINT_PREFIX}/check-organizations-access`,
                method: "GET",
            }),
        }),
        updateOrganization: build.mutation<IUpdateOrganizationResponse, IUpdateOrganizationBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.organizationId}`,
                method: "PUT",
                body: data.data,
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Organization", id: arg.organizationId }, "Organizations"],
        }),
        setOrganizationManagers: build.mutation<ISetOrganizationManagersResponse, ISetOrganizationManagersBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/set-organization-managers/${data.organizationId}`,
                method: "PATCH",
                body: {
                    managerIds: data.managerIds,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Organization", id: arg.organizationId }, "Organizations"],
        }),
        setOrganizationProjects: build.mutation<ISetOrganizationProjectsResponse, ISetOrganizationProjectsBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/set-organization-projects/${data.organizationId}`,
                method: "PATCH",
                body: {
                    projectIds: data.projectIds,
                },
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Organization", id: arg.organizationId }, "Organizations"],
        }),
        deleteOrganization: build.mutation<IDeleteOrganizationResponse, IDeleteOrganizationBody>({
            query: data => ({
                url: `${ENDPOINT_PREFIX}/${data.id}`,
                method: "DELETE",
            }),
            invalidatesTags: (_result, _error, arg) => [{ type: "Organization", id: arg.id }, "Organizations"],
        }),
        getOrganizationEmailReports: build.query<IGetOrganizationEmailReportsResponse, { organizationId: string; type?: string; latest?: boolean }>({
            query: ({ organizationId, type = "weekly report", latest = false }) => {
                const params = new URLSearchParams({ type });
                if (latest) params.append("latest", "true");

                return {
                    url: `${ENDPOINT_PREFIX}/organization-email-reports/${organizationId}?${params.toString()}`,
                    method: "GET",
                };
            },
            providesTags: (_res, _err, arg) => [{ type: "Organization", id: arg.organizationId }],
        }),
        sendOrganizationWeeklyReport: build.mutation<{ ok: boolean }, { organizationId: string }>({
            query: ({ organizationId }) => ({
                url: `${ENDPOINT_PREFIX}/send-organization-weekly-report/${organizationId}`,
                method: "POST",
            }),
            invalidatesTags: (_res, _err, arg) => [{ type: "Organization", id: arg.organizationId }],
        }),
        getOrganizationWeeklyReportSchedule: build.query<
            { id: string; name: string; repeatInterval: string; repeatTimezone?: string; nextRunAt?: string; lastRunAt?: string },
            { organizationId: string }
        >({
            query: ({ organizationId }) => ({
                url: `jobs/organization-weekly-report-schedule/${organizationId}`,
                method: "GET",
            }),
            providesTags: (_res, _err, arg) => [{ type: "Organization", id: arg.organizationId }],
        }),
        setOrganizationWeeklyReportSchedule: build.mutation<
            { id: string; name: string; repeatInterval: string; repeatTimezone?: string; nextRunAt?: string; lastRunAt?: string },
            { organizationId: string; data: { weekday: string; time: string; timezone: string } }
        >({
            query: ({ organizationId, data }) => ({
                url: `jobs/organization-weekly-report-schedule/${organizationId}`,
                method: "POST",
                body: data,
            }),
            invalidatesTags: (_res, _err, arg) => [{ type: "Organization", id: arg.organizationId }],
        }),
        deleteOrganizationWeeklyReportSchedule: build.mutation<{ ok: boolean; cancelled: number }, { organizationId: string }>({
            query: ({ organizationId }) => ({
                url: `jobs/organization-weekly-report-schedule/${organizationId}`,
                method: "DELETE",
            }),
            invalidatesTags: (_res, _err, arg) => [{ type: "Organization", id: arg.organizationId }],
        }),
    }),
});

export const {
    useGetOrganizationsQuery,
    useCheckOrganizationsAccessQuery,
    useCreateOrganizationMutation,
    useGetOrganizationByIdQuery,
    useUpdateOrganizationMutation,
    useSetOrganizationManagersMutation,
    useSetOrganizationProjectsMutation,
    useDeleteOrganizationMutation,
    useSendOrganizationWeeklyReportMutation,
    useGetOrganizationEmailReportsQuery,
    useGetOrganizationWeeklyReportScheduleQuery,
    useSetOrganizationWeeklyReportScheduleMutation,
    useDeleteOrganizationWeeklyReportScheduleMutation,
} = organizationsApi;
